import React from 'react';
import { ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import Text from '../global/Text';
import fontMaker from '../../utils/font';
import { colors } from '../../constants/theme';
import { IS_SHORT_VIEWPORT } from '../../utils/screensize';
import type { ViewByMode } from '../../typing/scheduleItem';

interface ViewByChipsProps {
  activeView: ViewByMode;
  onViewChange: (mode: ViewByMode) => void;
}

const VIEW_OPTIONS: ViewByMode[] = ['all', 'location', 'status', 'type'];

const ViewByChips: React.FC<ViewByChipsProps> = ({ activeView, onViewChange }) => {
  const { t } = useTranslation('home');

  const labelMap: Record<ViewByMode, string> = {
    all: t('newHome.viewByAll'),
    location: t('newHome.viewByLocation'),
    status: t('newHome.viewByStatus'),
    type: t('newHome.viewByType')
  };

  return (
    <View style={styles.container}>
      <Text style={styles.label}>{t('newHome.viewBy')}</Text>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.chips}>
        {VIEW_OPTIONS.map(mode => {
          const isActive = activeView === mode;
          return (
            <TouchableOpacity
              key={mode}
              activeOpacity={0.8}
              style={[styles.chip, isActive ? styles.chipActive : styles.chipInactive]}
              onPress={() => onViewChange(mode)}
            >
              <Text style={[styles.chipText, isActive ? styles.chipTextActive : styles.chipTextInactive]}>
                {labelMap[mode]}
              </Text>
            </TouchableOpacity>
          );
        })}
      </ScrollView>
    </View>
  );
};

export default React.memo(ViewByChips);

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 16,
    marginBottom: IS_SHORT_VIEWPORT ? 6 : 12
  },
  label: {
    ...fontMaker({ weight: 'Normal' }),
    fontSize: 13,
    color: colors.darkgray,
    marginRight: 8
  },
  chips: {
    flexDirection: 'row',
    alignItems: 'center'
  },
  chip: {
    paddingHorizontal: 16,
    paddingVertical: 6,
    borderRadius: 18,
    marginRight: 8,
    borderWidth: 1
  },
  chipActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary
  },
  chipInactive: {
    backgroundColor: 'transparent',
    borderColor: colors.lightgray
  },
  chipText: {
    ...fontMaker({ weight: 'SemiBold' }),
    fontSize: 13
  },
  chipTextActive: {
    color: '#fff'
  },
  chipTextInactive: {
    color: colors.darkgray
  }
});
